import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import LoadingButton from '@mui/lab/LoadingButton';
import { Box, Button, Chip, Grid, Stack, TextField, Typography, useTheme } from '@mui/material';
import { useDialog } from 'muibox';
import { useEffect, useState } from 'react';

interface AllowedUsernamesManagerProps {
    restrictMentionsEnabled: boolean;
    initialUsernames: string[];
    onSave: (usernames: string[]) => Promise<{ allowedUsernames: string[] }>;
    saveLoading: boolean;
}

const AllowedUsernamesManager = ({ restrictMentionsEnabled, initialUsernames, onSave, saveLoading }: AllowedUsernamesManagerProps) => {
    const theme = useTheme();
    const dialog = useDialog();
    const [inputValue, setInputValue] = useState<string>('');
    const [allowedUsernames, setAllowedUsernames] = useState<string[]>([]);

    // Initialize with provided usernames and sort them alphabetically
    useEffect(() => {
        if (initialUsernames) {
            const sortedUsernames = [...initialUsernames].sort((a, b) => a.localeCompare(b));
            setAllowedUsernames(sortedUsernames);
        }
    }, [initialUsernames]);

    // Sort usernames alphabetically whenever the list changes
    const sortUsernames = (usernames: string[]) => {
        return [...usernames].sort((a, b) => a.localeCompare(b));
    };

    // Check if the current list has changed from the original
    const hasChanged = () => {
        if (!initialUsernames) return allowedUsernames.length > 0;

        const sortedInitial = [...initialUsernames].sort((a, b) => a.localeCompare(b));
        const sortedCurrent = [...allowedUsernames].sort((a, b) => a.localeCompare(b));

        if (sortedInitial.length !== sortedCurrent.length) return true;

        return sortedInitial.some((username, index) => username !== sortedCurrent[index]);
    };

    // Clean username input - remove @ if present, trim, lowercase
    const cleanUsername = (input: string) => {
        return input.trim().toLowerCase().replace(/^@/, '');
    };

    const handleAddUsername = () => {
        const cleanedUsername = cleanUsername(inputValue);

        // Validate input
        if (!cleanedUsername) {
            return;
        }

        // Basic username validation (alphanumeric, underscores, dots, hyphens)
        const usernameRegex = /^[a-zA-Z0-9._-]+$/;
        if (!usernameRegex.test(cleanedUsername)) {
            dialog.alert({
                title: 'Invalid Username',
                message: 'Username can only contain letters, numbers, dots, underscores, and hyphens.',
            });
            return;
        }

        // Check for duplicates
        if (allowedUsernames.includes(cleanedUsername)) {
            dialog.alert({
                title: 'Duplicate Username',
                message: 'This username is already in the allowed list.',
            });
            return;
        }

        // Add username and sort the list
        const updatedUsernames = sortUsernames([...allowedUsernames, cleanedUsername]);
        setAllowedUsernames(updatedUsernames);
        setInputValue('');
    };

    const handleRemoveUsername = (usernameToRemove: string) => {
        const updatedUsernames = allowedUsernames.filter(username => username !== usernameToRemove);
        setAllowedUsernames(updatedUsernames);
    };

    const handleClearAll = () => {
        if (allowedUsernames.length === 0) return;

        dialog
            .confirm({
                title: 'Confirm',
                message: 'Are you sure you want to remove all allowed usernames?',
            })
            .then(() => {
                setAllowedUsernames([]);
            })
            .catch(() => {});
    };

    const handleSave = () => {
        onSave(allowedUsernames);
    };

    const handleKeyPress = (event: React.KeyboardEvent) => {
        if (event.key === 'Enter') {
            handleAddUsername();
        }
    };

    return (
        <Stack spacing={2}>
            {/* Description */}
            <Typography variant="body1">
                Protect your account by restricting mentions to only a specific list of usernames/creators. When enabled, only the usernames
                you add below will be allowed to be mentioned in messages. This includes @ mentions as well as Free Trial Links and Tracking
                Links!
            </Typography>
            <Typography variant="body1">
                Add usernames that are allowed to be mentioned when restrict mentions is enabled. Only these users will be allowed to be
                mentioned in messages when the restriction is active.
            </Typography>
            <Typography variant="body1">
                Please note: If a creator changes their username, you will need to update it in this list to continue allowing mentions.
            </Typography>

            {/* Input Section */}
            <Stack direction="column" spacing={0}>
                <Stack direction="row" spacing={1} alignItems="center">
                    <TextField
                        variant="outlined"
                        fullWidth
                        value={inputValue}
                        onChange={e => setInputValue(e.target.value.replace('@', ''))}
                        onKeyPress={handleKeyPress}
                        placeholder="Enter username (without @)..."
                        InputProps={{
                            startAdornment: <Typography color="text.secondary">@</Typography>,
                        }}
                    />
                    <Button
                        variant="contained"
                        color="primary"
                        size="large"
                        onClick={handleAddUsername}
                        disabled={!inputValue.trim() || inputValue.includes(',') || inputValue.includes('@') || inputValue.includes(';')}
                        startIcon={<AddIcon />}
                        sx={{ minWidth: 'auto', px: 2 }}
                    >
                        Add
                    </Button>
                </Stack>
            </Stack>

            {/* Statistics and Clear Button */}
            <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="body2" color="text.secondary">
                    Allowed Usernames: {allowedUsernames.length.toLocaleString()}
                </Typography>
                <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    onClick={handleClearAll}
                    disabled={allowedUsernames.length === 0}
                    startIcon={<DeleteIcon />}
                >
                    Clear All
                </Button>
            </Stack>

            {/* Usernames Display as Chips */}
            {allowedUsernames.length > 0 ? (
                <Box
                    sx={{
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: theme.shape.borderRadius,
                        p: 2,
                        backgroundColor: theme.palette.grey[50],
                        maxHeight: '300px',
                        overflowY: 'auto',
                    }}
                >
                    <Grid container spacing={1}>
                        {allowedUsernames.map(username => (
                            <Grid item key={username}>
                                <Chip
                                    label={`@${username}`}
                                    onDelete={() => handleRemoveUsername(username)}
                                    color="primary"
                                    variant="outlined"
                                    size="small"
                                    icon={<PersonAddIcon />}
                                />
                            </Grid>
                        ))}
                    </Grid>
                </Box>
            ) : (
                <Box
                    sx={{
                        border: `1px dashed ${theme.palette.divider}`,
                        borderRadius: theme.shape.borderRadius,
                        p: 4,
                        textAlign: 'center',
                        backgroundColor: restrictMentionsEnabled ? theme.palette.grey[50] : theme.palette.grey[100],
                        opacity: restrictMentionsEnabled ? 1 : 0.6,
                    }}
                >
                    <Typography variant="body2" color="text.secondary">
                        No allowed usernames added yet. Add some usernames above to get started.
                    </Typography>
                </Box>
            )}

            {/* Save Button */}
            <LoadingButton
                variant="contained"
                color={hasChanged() ? 'warning' : 'primary'}
                size="large"
                fullWidth
                loading={saveLoading}
                onClick={handleSave}
            >
                <span>Save Allowed Usernames</span>
            </LoadingButton>
        </Stack>
    );
};

export default AllowedUsernamesManager;
